import { ArrowRightOnRectangle, ArrowUpRightOnBox, ChevronRight, Language, Star, StarSolid } from "@medusajs/icons"
import { Link } from "react-router-dom"
import { GlobalSearch } from "../components/global-search"
import { PageHeader } from "../components/page"
import { useAuth } from "../lib/auth"
import { caps, LANGUAGES, t, useLocale } from "../lib/i18n"
import { medusaAdminUrl } from "../lib/medusa"
import { useFavourites } from "../lib/favourites"
import { NAV, NAV_GROUPS, type NavItem } from "../lib/nav"

/**
 * The phone's fifth tab: every section in its group, each saying in one line what it holds, and a
 * search that finds sections, orders, customers and products — so nothing has to be remembered.
 */
export function MorePage() {
  return (
    <div className="flex flex-col gap-y-6">
      <PageHeader title={t("More")} subtitle={t("Every part of HQ. Search finds orders, customers and products too.")} />
      <MoreContent />
    </div>
  )
}

/** Favourites first, then every section in its group, then the account: the More page and the side sheet. */
export function MoreContent() {
  const { user, signOut } = useAuth()
  const { locale } = useLocale()
  const favourites = useFavourites()
  return (
    <div className="flex flex-col gap-y-6">
      <GlobalSearch />

      <section className="flex flex-col gap-y-2" aria-labelledby="more-favourites">
        <h2 id="more-favourites" className="hc-eyebrow px-1">{caps(t("Favourites"))}</h2>
        {favourites.items.length ? (
          <SectionList items={favourites.items} favourites={favourites} />
        ) : (
          <p className="txt-small text-ui-fg-muted px-1">{t("Tap the star beside the pages you use most: they come up here, and as quick links at the bottom of Today.")}</p>
        )}
      </section>

      {NAV_GROUPS.map((group) => (
        <section key={group.key} className="flex flex-col gap-y-2">
          <h2 className="hc-eyebrow px-1">{caps(t(group.label))}</h2>
          <SectionList items={NAV.filter((item) => item.group === group.key)} favourites={favourites} />
        </section>
      ))}

      <section className="flex flex-col gap-y-2">
        <h2 className="hc-eyebrow px-1">{caps(t("Account"))}</h2>
        <ul className="bg-ui-bg-base shadow-elevation-card-rest divide-ui-border-base flex flex-col divide-y overflow-hidden rounded-lg">
          <li>
            <Link to="/settings#language" className="hover:bg-ui-bg-base-hover flex min-h-[52px] items-center gap-x-3 px-3 py-2.5">
              <span className="bg-ui-bg-subtle flex size-9 shrink-0 items-center justify-center rounded-md">
                <Language />
              </span>
              <span className="min-w-0 flex-1">
                <span className="txt-compact-medium-plus block">{t("Language")}</span>
                <span className="txt-compact-small text-ui-fg-muted block truncate">{LANGUAGES.find((language) => language.code === locale)?.name}</span>
              </span>
              <ChevronRight className="text-ui-fg-muted shrink-0" />
            </Link>
          </li>
          <li>
            <a href={medusaAdminUrl()} target="_blank" rel="noreferrer" className="hover:bg-ui-bg-base-hover flex min-h-[52px] items-center gap-x-3 px-3 py-2.5">
              <span className="bg-ui-bg-subtle flex size-9 shrink-0 items-center justify-center rounded-md">
                <ArrowUpRightOnBox />
              </span>
              <span className="min-w-0 flex-1">
                <span className="txt-compact-medium-plus block">{t("Medusa admin")}</span>
                <span className="txt-compact-small text-ui-fg-muted block truncate">{t("For edits HQ does not do yet")}</span>
              </span>
            </a>
          </li>
          <li>
            <button type="button" onClick={() => void signOut()} className="hover:bg-ui-bg-base-hover flex min-h-[52px] w-full items-center gap-x-3 px-3 py-2.5 text-left">
              <span className="bg-ui-bg-subtle flex size-9 shrink-0 items-center justify-center rounded-md">
                <ArrowRightOnRectangle />
              </span>
              <span className="min-w-0 flex-1">
                <span className="txt-compact-medium-plus block">{t("Sign out")}</span>
                <span className="txt-compact-small text-ui-fg-muted block truncate">{user?.email}</span>
              </span>
            </button>
          </li>
        </ul>
      </section>
    </div>
  )
}

/** A card of sections, each opening its page, with a star to keep it among the favourites. */
function SectionList({ items, favourites }: { items: NavItem[]; favourites: ReturnType<typeof useFavourites> }) {
  return (
    <ul className="bg-ui-bg-base shadow-elevation-card-rest divide-ui-border-base flex flex-col divide-y overflow-hidden rounded-lg">
      {items.map((item) => {
        const starred = favourites.isFavourite(item.to)
        const label = t(item.label)
        return (
          <li key={item.to} className="flex items-stretch">
            <Link to={item.to} className="hover:bg-ui-bg-base-hover active:bg-ui-bg-base-pressed flex min-h-[60px] min-w-0 flex-1 items-center gap-x-3 py-2.5 pl-3">
              <span className="bg-ui-bg-subtle text-ui-fg-base flex size-9 shrink-0 items-center justify-center rounded-md">
                <item.icon />
              </span>
              <span className="min-w-0 flex-1">
                <span className="txt-compact-medium-plus block">{label}</span>
                <span className="txt-small text-ui-fg-muted block leading-snug">{t(item.description)}</span>
              </span>
              <ChevronRight className="text-ui-fg-muted shrink-0" />
            </Link>
            {favourites.canStar(item.to) && (
              <button
                type="button"
                onClick={() => favourites.toggle(item.to)}
                aria-pressed={starred}
                aria-label={starred ? t("Remove {page} from favourites", { page: label }) : t("Add {page} to favourites", { page: label })}
                className={`hover:bg-ui-bg-base-hover flex w-12 shrink-0 items-center justify-center ${starred ? "text-ui-fg-interactive" : "text-ui-fg-muted"}`}
              >
                {starred ? <StarSolid /> : <Star />}
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
